/* ═══════════════════════════════════════════════════════════════
   The Designer — the quiet history.

   Design phase has no issuance to diff against, so the Designer keeps
   rolling safety snapshots instead (issuance.ts, kind:'auto' — capped,
   hidden from the issuance timeline). This panel is their visible entry
   point: recent snapshots with timestamps, view one read-only, or
   restore it. Restore goes through the editor as a NORMAL edit — it
   lands in the undo history and autosaves like any keystroke, so it is
   itself recoverable.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { createPortal } from 'react-dom';
import type { Issuance, ProjectSpec } from '../../shared/types';
import { autoSnapshots } from './issuance';
import { appLayerProps } from '../components/ui/app-layer';

const PANEL_W = 300;

function timeAgo(iso?: string): string {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  if (diff < 604_800_000) return `${Math.floor(diff / 86_400_000)}d ago`;
  return new Date(iso).toLocaleDateString();
}

export interface HistoryPanelProps {
  anchor: { left: number; bottom: number; right: number };
  spec: ProjectSpec;
  canEdit: boolean;
  /** Open this snapshot read-only on the canvas. */
  onView: (snapshot: Issuance) => void;
  /** Put this snapshot's content back into the editor (undoable edit). */
  onRestore: (snapshot: Issuance) => void;
  onClose: () => void;
}

export function HistoryPanel({
  anchor,
  spec,
  canEdit,
  onView,
  onRestore,
  onClose,
}: HistoryPanelProps) {
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [onClose]);

  React.useEffect(() => {
    ref.current?.querySelector<HTMLButtonElement>('button')?.focus();
  }, []);

  // Newest first — the one you want back is almost always the recent one.
  const snaps = React.useMemo(() => [...autoSnapshots(spec)].reverse(), [spec]);

  const left = Math.max(8, Math.min(anchor.right - PANEL_W, window.innerWidth - PANEL_W - 8));
  const maxH = Math.min(480, window.innerHeight - anchor.bottom - 16);

  return createPortal(
    <div
      {...appLayerProps}
      ref={ref}
      className="dsg-menu dsg-history"
      role="menu"
      aria-label="Document history"
      style={{ left, top: anchor.bottom + 6, width: PANEL_W, maxHeight: maxH }}
    >
      <div className="dsg-history-head">Snapshots</div>
      <div className="dsg-history-sub">
        SpecForma takes one automatically every few minutes while this section changes. Issuing the
        manual starts a fresh set.
      </div>
      {snaps.length === 0 ? (
        <div className="dsg-history-empty" role="status">
          No snapshots yet — keep writing and one will appear here.
        </div>
      ) : (
        snaps.map((s) => (
          <div key={s.id} className="dsg-history-row">
            <div className="dsg-history-meta">
              <span className="dsg-history-time">{timeAgo(s.createdAt)}</span>
              <span className="dsg-history-who">
                {new Date(s.createdAt || 0).toLocaleString([], {
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                })}
                {s.issuedBy ? ` · ${s.issuedBy}` : ''}
              </span>
            </div>
            <div className="dsg-history-actions">
              <button
                type="button"
                className="dsg-history-btn"
                onClick={() => {
                  onClose();
                  onView(s);
                }}
              >
                View
              </button>
              <button
                type="button"
                className="dsg-history-btn"
                disabled={!canEdit}
                title="Put this snapshot's text back — undoable with Ctrl+Z"
                onClick={() => {
                  onClose();
                  onRestore(s);
                }}
              >
                Restore
              </button>
            </div>
          </div>
        ))
      )}
    </div>,
    document.body
  );
}
